import { IEventInformationData, IItinerarymap, tryParseJSON } from "@eventr/common";
import { Capacitor } from "@capacitor/core";
import { ColoursObj } from "@eventr/common-storybook";
import { Box } from "@mui/material";
import { toneOfImageStrip, useStatusBarTone } from "feat/appState/statusBarTone";
import { FC, useCallback, useMemo } from "react";
import { HEADER_HEIGHT } from "state/scrollState/scrollContext";
import { useTopInset, useWindowStore } from "state/windowState/window.store";
import { CarouselSlideBackground } from "./CarouselSlideBackground";
import { ItineraryDateWindow } from "./ItineraryDateWindow";
import { ItineraryTitle } from "./ItineraryTitle";
import { OrganisationTag } from "./OrganisationTag";
import { coverImageUrl } from "feat/media/cover-image";

type TCarouselSlide = {
  id?: string;
  slidesInView: number[];
  index: number;
  itineraryMap: IItinerarymap;
};

export const CarouselSlide: FC<TCarouselSlide> = ({ slidesInView, index, itineraryMap }) => {
  const topInset = useTopInset();
  // The status bar alone, which the picture is sampled under: a view's inset can also hold the
  // iPad's floating tab bar.
  const statusBarHeight = useWindowStore((s) => s.windowSizes.insets?.insets.top ?? 0);

  const itineraryInformation: IEventInformationData = useMemo(() => {
    if (!itineraryMap || !itineraryMap.value) return undefined;

    try {
      if (typeof itineraryMap.value === "string") {
        return tryParseJSON(itineraryMap.value, {
          OSMAddress: {},
          title: "EventR",
          start_date: "",
          end_date: "",
          cover_image: "",
          website: "",
          additionFiles: [],
        });
      }
      return itineraryMap.value;
    } catch {
      console.log("error parsing value", itineraryMap.value);
      return undefined;
    }
  }, [itineraryMap]);

  // iOS picks the status bar's text against this picture while the header is under the bar.
  // The slide's gradient washes the top of it faintly in the header colour; at the middle of
  // the strip it is about a fifth of the way from its top alpha to its bottom one.
  const itineraryId = itineraryMap?.id;
  const focus = itineraryInformation?.cover_image ? itineraryInformation.cover_image_focus : undefined;
  const onPainted = useCallback(
    (url: string, frame: { width: number; height: number }) => {
      if (Capacitor.getPlatform() !== "ios" || itineraryId === undefined) return;
      const mid = statusBarHeight / 2 / frame.height;
      toneOfImageStrip({
        url,
        focus,
        frameWidth: frame.width,
        frameHeight: frame.height,
        stripHeight: statusBarHeight,
        wash: { colour: ColoursObj.backgrounds.header, alpha: 0x10 / 255 + ((0xd9 - 0x10) / 255) * mid },
      }).then((tone) => {
        if (tone) useStatusBarTone.getState().setImageTone(String(itineraryId), tone);
      });
    },
    [focus, itineraryId, statusBarHeight],
  );

  return (
    <div
      style={{
        flex: "0 0 100%",
        minWidth: "0",
        height: "100%",
      }}
    >
      <CarouselSlideBackground
        // The cover image is always declared; `load` is what the lazy gate controls. Passing
        // `undefined` for an off-screen slide used to read as "this itinerary has no cover
        // image", so every slide painted the fallback splash first.
        src={coverImageUrl(itineraryInformation, "large")}
        load={slidesInView.includes(index)}
        focus={itineraryInformation?.cover_image_focus}
        onPainted={onPainted}
      >
        <Box
          sx={{
            display: "grid",
            // Kept in the column when the picture runs on under a vertical bar.
            padding: `${topInset}px calc(8px + var(--header-bleed-right, 0px)) 0px calc(8px + var(--header-bleed-left, 0px))`,
            // A definite height rather than `height: 100%` clamped by a `maxHeight`. Those
            // two disagreed — 100% of the header is HEADER_HEIGHT + topInset, the cap is 96
            // — and with content-box sizing the rows below resolved against whichever won
            // in a given layout pass. Under iOS native navigation every tab renders its own
            // copy of this header and Chat's and Team's first lay out while their webview is
            // still off screen, where the cap lost: the rows came out 10px over it and the
            // title sat 13px lower than the same header drawn on Home.
            height: HEADER_HEIGHT - 60,
          }}
        >
          <Box
            sx={{
              display: "grid",
              // `min-content`, not `auto`: an `auto` track may be grown past its contents
              // when the tracks are sized, which is what put 16px of dead space under the
              // organisation tag and pushed the title down.
              gridTemplateRows: "min-content 1fr min-content",
              height: "100%",
              alignItems: "center",
              justifyItems: "center",
              padding: "8px 0 0 0",
            }}
          >
            {/* The tag and the date window are placed directly rather than through a
                VerticalExpander. `open` was passed as a constant, so the expander never
                animated anything; all it contributed was an inner `1fr` track with no
                definite height to resolve against, which is the ambiguity the row sizes
                above were picking up. */}
            <OrganisationTag />
            <ItineraryTitle id="carouselslide-" name={itineraryInformation?.title || ""} />
            <ItineraryDateWindow
              id="carouselslide-"
              start={itineraryInformation?.start_date}
              end={itineraryInformation?.end_date}
              tags={itineraryMap?.tags}
            />
          </Box>
        </Box>
      </CarouselSlideBackground>
    </div>
  );
};
